import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { Input, Button } from 'components/ui'
import { HiOutlineSearch, HiOutlineFilter } from 'react-icons/hi'
import { btnPrimaryStyle, btnOutlineStyle } from 'constants/brand.constant'

/**
 * List toolbar:
 * search (+ optional count) on the left;
 * inline filters / Sort / secondary controls, then primary + Add on the right.
 */
const ListToolbar = ({
	search,
	searchPlaceholder = 'Search…',
	searchValue,
	onSearchChange,
	resultCount,
	filters,
	sort,
	secondaryActions,
	primaryAction,
	onFiltersClick,
	filtersLabel = 'Filters',
	filtersActive = false,
	className,
}) => {
	const showSearch =
		search !== undefined && search !== null
			? search
			: onSearchChange
				? (
					<Input
						className="sw-search-field w-full sm:w-64 md:w-72"
						size="sm"
						placeholder={searchPlaceholder}
						prefix={<HiOutlineSearch className="text-lg text-gray-400" />}
						value={searchValue ?? ''}
						onChange={onSearchChange}
					/>
				)
				: null

	const filtersControl =
		filters !== undefined && filters !== null
			? filters
			: onFiltersClick
				? (
					<Button
						size="sm"
						variant="default"
						icon={<HiOutlineFilter />}
						className={classNames('sw-btn-outline', filtersActive && 'sw-btn-outline-active')}
						style={btnOutlineStyle}
						onClick={onFiltersClick}
					>
						{filtersLabel}
					</Button>
				)
				: null

	const primary =
		primaryAction && React.isValidElement(primaryAction)
			? React.cloneElement(primaryAction, {
					size: primaryAction.props.size || 'sm',
					variant: primaryAction.props.variant || 'solid',
					className: classNames('!text-white sw-btn-primary', primaryAction.props.className),
					style: { ...btnPrimaryStyle, ...primaryAction.props.style },
				})
			: primaryAction

	const normalizeAction = (node) => {
		if (!React.isValidElement(node)) return node
		if (node.type === React.Fragment) {
			return React.cloneElement(node, {
				children: React.Children.map(node.props.children, normalizeAction),
			})
		}
		const isButtonLike =
			typeof node.type === 'string'
				? node.type === 'button'
				: true
		if (!isButtonLike) return node
		return React.cloneElement(node, {
			size: node.props.size || 'sm',
			className: classNames(node.props.className),
		})
	}

	return (
		<div className={classNames('sw-list-toolbar', className)}>
			<div className="sw-list-toolbar-left">
				{showSearch}
				{resultCount != null && resultCount !== '' ? (
					<span className="sw-result-count">{resultCount}</span>
				) : null}
			</div>
			<div className="sw-list-toolbar-right">
				{filtersControl}
				{sort}
				{normalizeAction(secondaryActions)}
				{primary}
			</div>
		</div>
	)
}

ListToolbar.propTypes = {
	search: PropTypes.node,
	searchPlaceholder: PropTypes.string,
	searchValue: PropTypes.string,
	onSearchChange: PropTypes.func,
	resultCount: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
	filters: PropTypes.node,
	sort: PropTypes.node,
	secondaryActions: PropTypes.node,
	primaryAction: PropTypes.node,
	onFiltersClick: PropTypes.func,
	filtersLabel: PropTypes.string,
	filtersActive: PropTypes.bool,
	className: PropTypes.string,
}

export default ListToolbar
